"use strict";
/**
 * Replay compaction for stream resume.
 *
 * Reconnecting to an in-flight generation replays the ENTIRE buffered stream.
 * useChat processes every chunk with an immutable state update over a growing
 * message — O(n²) work. A long build turn replays thousands of chunks (one
 * text-delta per token, one tool-input-delta per fragment), which pegged the
 * browser's renderer for minutes and froze EVERY tab of the app (they share
 * one renderer process). `experimental_throttle` only throttles React renders,
 * not chunk processing — so the fix is to never send the firehose: fold the
 * replayed burst into equivalent-but-few chunks server-side, then pass live
 * chunks through untouched.
 *
 * Folding rules (strictly order-preserving):
 *  - consecutive `text-delta` chunks with the same id merge into one
 *  - consecutive `reasoning-delta` chunks with the same id merge into one
 *  - `tool-input-delta` chunks are DROPPED when a later `tool-input-available`
 *    for the same toolCallId is present in the folded window (the available
 *    chunk carries the complete input); without one (input still streaming at
 *    the fold boundary) the deltas are kept, merged consecutively
 *  - everything else passes through unchanged, in its original position
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseSseBlock = parseSseBlock;
exports.toSseBlock = toSseBlock;
exports.foldChunks = foldChunks;
exports.foldReplaySse = foldReplaySse;
/** Parse an SSE block ("data: {...}\n\n") into its JSON payload, or null. */
function parseSseBlock(block) {
    const dataLines = block
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart());
    if (dataLines.length === 0) {
        return null;
    }
    try {
        return JSON.parse(dataLines.join('\n'));
    }
    catch {
        return null;
    }
}
/** Serialize a chunk back to an SSE block. */
function toSseBlock(chunk) {
    return `data: ${JSON.stringify(chunk)}\n\n`;
}
/** Fold a window of parsed UIMessage chunks (see module docs for rules). */
function foldChunks(chunks) {
    // Pass 1: which tool calls have their complete input in this window?
    const completedToolInputs = new Set();
    for (const c of chunks) {
        if (c?.type === 'tool-input-available' && c.toolCallId) {
            completedToolInputs.add(c.toolCallId);
        }
    }
    const out = [];
    for (const c of chunks) {
        const prev = out[out.length - 1];
        if (c?.type === 'text-delta' && typeof c.delta === 'string') {
            if (prev?.type === 'text-delta' && prev.id === c.id && typeof prev.delta === 'string') {
                prev.delta += c.delta;
                continue;
            }
            out.push({ ...c });
            continue;
        }
        if (c?.type === 'reasoning-delta' && typeof c.delta === 'string') {
            if (prev?.type === 'reasoning-delta' && prev.id === c.id && typeof prev.delta === 'string') {
                prev.delta += c.delta;
                continue;
            }
            out.push({ ...c });
            continue;
        }
        if (c?.type === 'tool-input-delta' && c.toolCallId && completedToolInputs.has(c.toolCallId)) {
            continue; // the complete input follows — the per-fragment deltas are noise
        }
        if (c?.type === 'tool-input-delta' && typeof c.inputTextDelta === 'string') {
            // Input still streaming at the fold boundary — keep it, merged.
            if (prev?.type === 'tool-input-delta' && prev.toolCallId === c.toolCallId) {
                prev.inputTextDelta = (prev.inputTextDelta ?? '') + c.inputTextDelta;
                continue;
            }
            out.push({ ...c });
            continue;
        }
        out.push(c);
    }
    return out;
}
/**
 * Fold a raw replayed SSE string: parse its event blocks, fold the parsed
 * chunks, and serialize back. Unparseable blocks pass through in place (they
 * are counted as opaque markers so ordering survives). Returns the folded SSE
 * plus any trailing partial block (an event cut off mid-write at the
 * burst/live boundary) that the caller must prepend to the next live data.
 */
function foldReplaySse(raw) {
    const lastSep = raw.lastIndexOf('\n\n');
    if (lastSep === -1) {
        // No complete event yet — everything is remainder.
        return { folded: '', remainder: raw, before: 0, after: 0 };
    }
    const complete = raw.slice(0, lastSep + 2);
    const remainder = raw.slice(lastSep + 2);
    const blocks = complete.split('\n\n').filter((b) => b.trim().length > 0);
    const parsed = [];
    const OPAQUE = Symbol('opaque');
    const opaques = [];
    for (const b of blocks) {
        const p = parseSseBlock(b);
        if (p === null) {
            // Preserve unparseable blocks exactly, in order, via placeholders.
            parsed.push({ type: OPAQUE, __raw: opaques.length });
            opaques.push(b);
        }
        else {
            parsed.push(p);
        }
    }
    const folded = foldChunks(parsed);
    const sse = folded
        .map((c) => {
        const anyC = c;
        if (anyC.type === OPAQUE && typeof anyC.__raw === 'number') {
            return `${opaques[anyC.__raw]}\n\n`;
        }
        return toSseBlock(c);
    })
        .join('');
    return { folded: sse, remainder, before: blocks.length, after: folded.length };
}
